import { Alert, Box, FormControl, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { ModalContext } from "../../../shared/components";
import { DateViewMonthYear } from "../../../shared/components/DateViewMonthYear/DataViewMonthYear";
import LoadingDialog from "../../../shared/components/LoadingModalDialog/LoadingModalDialog";

const DECIMAL_COMMA_3DP_REGEX = /^\d+(,\d{0,3})?$/;

// Definição do esquema de validação do Yup
const validationSchema = Yup.object().shape({
	reference: Yup.date().required("Referência é obrigatória").typeError("Data inválida"),
	ipca: Yup.string()
		.required("IPCA é obrigatório")
		.matches(DECIMAL_COMMA_3DP_REGEX, "Use apenas números e vírgula, com até 3 casas decimais"),
	incc: Yup.string()
		.required("INCC é obrigatório")
		.matches(DECIMAL_COMMA_3DP_REGEX, "Use apenas números e vírgula, com até 3 casas decimais"),
	igpm: Yup.string()
		.required("IGPM é obrigatório")
		.matches(DECIMAL_COMMA_3DP_REGEX, "Use apenas números e vírgula, com até 3 casas decimais"),
	status: Yup.string().oneOf(["active", "inactive"], "Status inválido").required("Status é obrigatório"),
});

interface ModalReadJustProps {
	isOpen: boolean;
	onClose: () => void;
	data: any;
	isLoading?: boolean;
	onSave?: (data: any) => void | Promise<void>;
	mode?: "create" | "edit";
}

export const ModalReadJust: React.FC<ModalReadJustProps> = ({ isOpen, onClose, data, isLoading = false, onSave, mode = "edit" }) => {
	const sanitizeDecimalComma = (raw: string): string => {
		// Aceita somente dígitos e um separador decimal (vírgula ou ponto), padronizando para vírgula.
		let value = String(raw ?? "").replace(/\./g, ",");
		value = value.replace(/[^0-9,]/g, "");

		const [integerPart = "", fractionalRaw = ""] = value.split(",");
		const integerDigits = integerPart.replace(/[^0-9]/g, "");
		const fractionalDigits = fractionalRaw.replace(/[^0-9]/g, "").slice(0, 3);

		// Se o usuário digitou a vírgula, mantém ela (mesmo sem casas) para UX.
		const hadComma = value.includes(",");
		if (hadComma) return `${integerDigits}${","}${fractionalDigits}`;
		return integerDigits;
	};

	const normalizeIncomingPercent = (value: any): string => {
		if (value === null || value === undefined) return "";
		return sanitizeDecimalComma(String(value));
	};

	const coerceIncomingData = (incoming: any) => {
		const source = incoming ?? {};
		const rawStatus = String(source?.status ?? "active").toLowerCase();
		const status = rawStatus === "inactive" ? "inactive" : "active";
		return {
			...source,
			// backend pode devolver em lower ou UPPER
			ipca: normalizeIncomingPercent(source.ipca ?? source.IPCA),
			incc: normalizeIncomingPercent(source.incc ?? source.INCC),
			igpm: normalizeIncomingPercent(source.igpm ?? source.IPGM),
			reference: source?.reference ? dayjs(source.reference).toDate() : null,
			status,
		};
	};

	const [editedData, setEditedData] = useState<any>(coerceIncomingData(data));
	const [errors, setErrors] = useState<any>({});
	const [isLoadingData, setIsLoadingData] = useState(false);

	useEffect(() => {
		if (isOpen && data) {
			setIsLoadingData(true);
			setEditedData(coerceIncomingData(data));
		}
		setIsLoadingData(false);
	}, [isOpen, data]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setEditedData((prev: any) => ({ ...prev, [name]: sanitizeDecimalComma(value) }));
	};

	const handleStatusChange = (e: SelectChangeEvent<string>) => {
		const { value } = e.target;
		setEditedData((prev: any) => ({ ...prev, status: value }));
	};

	const handleReferenceChange = (value: Dayjs | null) => {
		setEditedData((prev: any) => ({ ...prev, reference: value ? value.toDate() : null }));
	};

	const handleSaveChanges = async () => {
		try {
			// Limpa os erros anteriores
			setErrors({});

			// Valida os dados utilizando Yup
			await validationSchema.validate(editedData, { abortEarly: false });

			// Se passar pela validação, executa a função de salvar
			if (onSave) {
				await onSave(editedData);
			}

			onClose(); // Fecha o modal após salvar
		} catch (validationErrors) {
			// Caso tenha erros, formata para serem exibidos no formulário
			if (validationErrors instanceof Yup.ValidationError) {
				const formattedErrors: any = {};
				validationErrors.inner.forEach((error) => {
					formattedErrors[error.path!] = error.message;
				});
				setErrors(formattedErrors);
			}
		}
	};

	return (
		<>
			{isLoadingData && <LoadingDialog isLoading={isLoadingData} loadingText={`Realizando análise...`} />}

			<ModalContext
				isOpenModal={isOpen}
				isCloseModal={onClose}
				messageType="info"
				textButtonCancel="Fechar"
				textButtonConfirm="Salvar"
				showButtonCancel={true}
				showButtonConfirm={true}
				ITitle={mode === "create" ? "Cadastrar Indice" : "Editar Indices"}
				onclickConfirm={handleSaveChanges}
				IMessage={[
					<Box sx={{ px: 2, py: 1.5 }}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="subtitle1" fontWeight={600}>
									Configuração do índice
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Defina o status, a referência (mês/ano) e os percentuais do reajuste.
								</Typography>
							</Grid>

							<Grid item xs={12} md={6}>
								<FormControl fullWidth size="small">
									<InputLabel id="readjust-status-label">Status</InputLabel>
									<Select
										labelId="readjust-status-label"
										label="Status"
										value={String(editedData?.status ?? "active")}
										onChange={handleStatusChange}
										disabled={isLoading}
									>
										<MenuItem value="active">Ativo</MenuItem>
										<MenuItem value="inactive">Inativo</MenuItem>
									</Select>
									{errors.status && <FormHelperText error>{errors.status}</FormHelperText>}
								</FormControl>
							</Grid>

							<Grid item xs={12} md={6}>
								<DateViewMonthYear
									title="Referência"
									value={editedData?.reference ? dayjs(editedData.reference) : null}
									onChange={(value) => handleReferenceChange(value ? dayjs(value) : null)}
									disabled={isLoading}
									sx={{ width: "100%" }}
								/>
								{errors.reference && <FormHelperText error>{errors.reference}</FormHelperText>}
							</Grid>

							{String(editedData?.status ?? "active").toLowerCase() === "inactive" && (
								<Grid item xs={12}>
									<Alert severity="warning">
										Ao desativar isso impactará em todas os ajustes de garantias renovadas e que tenha mais de 12 meses
									</Alert>
								</Grid>
							)}

							<Grid item xs={12}>
								<Typography variant="subtitle2" fontWeight={600}>
									Índices (%)
								</Typography>
							</Grid>

							<Grid item xs={12} md={4}>
								<TextField
									label="IPCA"
									name="ipca"
									type="text"
									size="small"
									placeholder="Ex: 10,125"
									value={editedData?.ipca || ""}
									fullWidth
									onChange={handleInputChange}
									error={Boolean(errors.ipca)}
									helperText={errors.ipca}
									inputProps={{ inputMode: "decimal", pattern: "^\\d+(,\\d{0,3})?$" }}
									InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									label="INCC"
									name="incc"
									type="text"
									size="small"
									placeholder="Ex: 9,750"
									value={editedData?.incc || ""}
									fullWidth
									onChange={handleInputChange}
									error={Boolean(errors.incc)}
									helperText={errors.incc}
									inputProps={{ inputMode: "decimal", pattern: "^\\d+(,\\d{0,3})?$" }}
									InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									label="IGPM"
									name="igpm"
									type="text"
									size="small"
									placeholder="Ex: 8,333"
									value={editedData?.igpm || ""}
									fullWidth
									onChange={handleInputChange}
									error={Boolean(errors.igpm)}
									helperText={errors.igpm}
									inputProps={{ inputMode: "decimal", pattern: "^\\d+(,\\d{0,3})?$" }}
									InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
								/>
							</Grid>
						</Grid>
					</Box>,
				]}
			/>
		</>
	);
};
